import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import {Box, CircularProgress, Skeleton} from "@mui/material";
import {useInfiniteQuery} from "@tanstack/react-query";
import {useDeviceDetach} from "hooks/useDeviceDetach";
import queryString from "query-string";
import React, {CSSProperties, ReactNode, useMemo} from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import {useLocation} from "react-router";
import {Buttons} from "./components/Buttons/Buttons";
import {SecureFab} from "components/Admin/Secure/SecureFab/SecureFab";
import {CustomOutlet} from "components/Admin/CustomOutlet/CustomOutlet";
import {BaseListHeader} from "./components/BaseListHeader/BaseListHeader";
import {RecordContextProvider, SimpleShowLayout, useNotify, useResourceContext} from "react-admin";

type ListProps<TRecord extends { id: any }> = {
  queryKey?: string;
  children: ((item: TRecord) => ReactNode) | ReactNode;
  queryLimit?: number;
  loader?: React.ReactNode;
  header?: React.ReactNode;
  getList: (props: {
    limit?: number;
    offset?: number;
    filter?: any;
  }) => Promise<TRecord[]>;
  buttons?: React.ReactNode;
};

export const List: <TRecord extends { id: any }>(
  props: ListProps<TRecord>
) => JSX.Element = (props) => {
  const location = useLocation();

  const notify = useNotify();

  const {isMobile} = useDeviceDetach();

  const queryLimit = useMemo(() => props.queryLimit ?? 10, [props.queryLimit]);

  const filter = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );

  const resource = useResourceContext();

  const queryKey = props.queryKey ?? resource;

  if (!queryKey) {
    throw new Error("Query key or resource key is required");
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    error,
  } = useInfiniteQuery({
    queryKey: [queryKey, "list", filter],
    queryFn: ({pageParam}) => {
      return props
        .getList({
          filter: filter,
          offset: pageParam.offset,
          limit: pageParam.limit,
        }).then((records) => {

          console.log("Return for params:", pageParam, "records:", records);

          return records;
        })
        .catch((error) => {
          console.error(error);
          notify(error.message ?? "Error", {
            type: "error",
          });

          return Promise.reject(error);
        });
    },
    initialPageParam: {offset: 0, limit: queryLimit},
    getNextPageParam: (lastPage, pages, lastPageParam) => {
      if (lastPage.length < lastPageParam.limit) {
        return undefined;
      }

      return {offset: pages.flat().length, limit: queryLimit};
    },
    // refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
  });

  const recordsView = useMemo(() => {
    return data?.pages?.flat()?.map((record) => {
      if (typeof props.children == "function") {
        return (
          <Box key={record.id} sx={{paddingX: "15px", paddingY: "5px"}}>
            {props.children(record)}
          </Box>
        )
      }

      return (
        <RecordContextProvider value={record}>
          <SimpleShowLayout>
            {props.children}
          </SimpleShowLayout>
        </RecordContextProvider>
      )
    });
  }, [data, props]);

  const loader = useMemo(() => {
    return Array(queryLimit)
      .fill(undefined)
      .map((_, index) => (
        <Box key={index} sx={{paddingX: "15px", paddingY: "5px"}}>
          {props.loader ?? <Skeleton height={50}/>}
        </Box>
      ));
  }, [props, queryLimit]);

  const buttons = useMemo(() => {
    return (
      props.buttons ?? (
        <>
          <SecureFab size="small" color="primary" href="./filter" strictCheck>
            <TuneIcon/>
          </SecureFab>
          <SecureFab color="primary" aria-label="add" href="./create" strictCheck>
            <AddIcon/>
          </SecureFab>
        </>
      )
    );
  }, [props.buttons]);

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
  });

  return (
    <>
      <CustomOutlet/>
      <Box
        sx={{
          ...styles.container,
          height: isMobile ? "100%" : "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Box>
            {props.header ?? <BaseListHeader/>}
            {isLoading ? loader : recordsView}
            {(isFetchingNextPage || !isFetching) && hasNextPage ? (
              <Box
                ref={sentryRef}
                sx={{
                  width: "100%",
                  height: "50px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircularProgress/>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Buttons buttons={buttons}/>
    </>
  );
};

const styles: { [key: string]: CSSProperties } = {
  container: {
    width: "100%",
    boxSizing: "border-box",
  },
};
